import { Injectable, inject } from '@angular/core';
import { AppContext } from '../models/AppContext';
import { DataService } from '../services/data.service';
import { EndpointService } from '../services/endpoint.service';
import { ParserService } from '../services/parser.service';
import { fhirR4 } from '@smile-cdr/fhirts';
import { TranslateService } from '@ngx-translate/core';
import * as crdClient from '@simpatico.ai/js-crd-client/src/index.js';
import { CrdResponse } from '../models/CrdResponse';
import * as libMarshaller from '@simpatico.ai/lib-marshaler';
import { ResourceType, supportedOrderTypes } from '../models/ResourceType';
import { EnvService } from '../services/env.service';

export abstract class CrdClientWrapper {
  static readonly isPriorAuthRequired = crdClient.isPriorAuthRequired;
}
@Injectable({
  providedIn: 'root',
})
export class ContextService {
  private readonly dataService = inject(DataService);
  private readonly endpointService = inject(EndpointService);
  private readonly parserService = inject(ParserService);
  private readonly translate = inject(TranslateService);
  private readonly env = inject(EnvService);

  static readonly smileOrgIdentifier =
    'http://smiledigitalhealth.com/pa-org-identifier';
  marshaller = new libMarshaller.MarshallerContainer();

  async getAppContext(): Promise<AppContext> {
    const client = await this.dataService.getClient();
    if (client?.getState('tokenResponse.appContext')) {
      return JSON.parse(
        client.getState('tokenResponse.appContext'),
      ) as AppContext;
    }
    if (client?.getState('tokenResponse.fhirContext')) {
      return {
        ...client.getState('tokenResponse'),
      };
    }
    if (client?.getState('tokenResponse.dtrOrdId')) {
      return {
        ...client.getState('tokenResponse'),
        fhirContext: [{ reference: client.getState('tokenResponse.dtrOrdId') }],
      };
    }
    if (client?.getState('tokenResponse.launchContext')) {
      const raw = client.getState('tokenResponse.launchContext');
      const fhirContext =
        typeof raw === 'string' ? JSON.parse(decodeURIComponent(raw)) : raw;
      return {
        ...client.getState('tokenResponse'),
        fhirContext,
      };
    }
    throw new Error(this.translate.instant('Error.NoAppContext'));
  }

  async marshalContext(appContext: AppContext) {
    return this.marshaller.marshal(
      appContext,
      await this.dataService.getServerUrl(),
      await this.dataService.getAuth(),
      false, //noauth
    );
  }

  async getOrderResource(appContext: AppContext): Promise<fhirR4.Resource> {
    if (appContext.fhirContext) {
      return await this.getOrderResourceFromMarshalContext(
        appContext.marshalContext,
      );
    } else return this.getOrderResourceFromAppContext(appContext);
  }

  getOrderResourceFromAppContext(appContext: AppContext): fhirR4.Resource {
    const bundle = appContext.context?.draftOrders;
    if (!bundle) {
      throw new Error(this.translate.instant('Error.NoDraftOrders'));
    }
    const resource = this.parserService.getFirstOrUndefined(
      bundle.entry,
    )?.resource;
    if (resource) {
      return resource;
    } else {
      throw new Error(this.translate.instant('Error.NoOrderRequest'));
    }
  }

  async getOrderResourceFromMarshalContext(
    marshalContext,
  ): Promise<fhirR4.Resource> {
    return await this.dataService.getResource(
      this.getOrderReferenceFromMarshalContext(marshalContext),
    );
  }

  getOrderReference(appContext: AppContext): string {
    if (appContext.fhirContext) {
      return this.getOrderReferenceFromMarshalContext(
        appContext.marshalContext,
      );
    }
    const resource = this.getOrderResourceFromAppContext(appContext);
    return `${resource.resourceType}/${resource.id}`;
  }

  getOrderReferenceFromMarshalContext(marshalContext): string {
    return (
      supportedOrderTypes
        .map((type) =>
          this.getReferenceFromMarshalContext(marshalContext, type),
        )
        .find(Boolean) ?? ''
    );
  }

  getReferenceFromMarshalContext(
    marshalContext,
    key: string,
  ): string | undefined {
    return marshalContext.marshalingRequest.references[key];
  }

  getPatientReference(appContext: AppContext): fhirR4.Reference {
    const patientId = appContext.fhirContext
      ? appContext.patient
      : appContext.context?.patientId;
    const reference = new fhirR4.Reference();
    reference.reference = `${ResourceType.Patient}/${patientId}`;
    return reference;
  }

  getCoverageId(appContext: AppContext): string {
    return appContext.fhirContext
      ? (this.getReferenceFromMarshalContext(
        appContext.marshalContext,
        'Coverage',
      ) ?? '')
      : this.getCoverageIdFromAppContext(appContext);
  }

  getCoverageIdFromAppContext(appContext: AppContext): string {
    const resource = this.getOrderResourceFromAppContext(appContext);
    return this.parserService.getCoverageId(resource);
  }

  async getPayorOrg(appContext: AppContext): Promise<fhirR4.Organization> {
    if (appContext.fhirContext) {
      return appContext.marshalContext?.marshalingRequest.Organization;
    } else {
      const coverageId = this.getCoverageIdFromAppContext(appContext);
      const coverage = await this.dataService.getResource(coverageId);
      return await this.dataService.getResource(coverage.payor[0].reference);
    }
  }

  async callCrd(appContext: AppContext, order: fhirR4.Resource): Promise<CrdResponse> {
    console.log('Calling CDS Hooks via EPA SaaS proxy...');
    return (
      await CrdClientWrapper.isPriorAuthRequired(
        [order],
        await this.getRequestParams(appContext),
        this.endpointService.getCdsServicesUrl(),
        this.env.environment.useHook,
        /* debug */ false,
        /* useProxy */ false,
        this.env.environment.supportedCrdVersions,
      )
    ).pop();
  }

  async getRequestParams(appContext: AppContext) {
    return {
      patientId: appContext.patient ?? appContext.context?.patientId,
      userId: this.dataService.fhirUser,
      hook: this.env.environment.useHook,
      fhirAuthorization: {
        access_token: appContext.access_token,
        token_type: 'Bearer',
        expires_in: appContext.expires_in,
        scope: appContext.scope,
        subject: this.env.environment.clientId,
        patient: appContext.patient,
      },
      cdsAuthToken: 'Bearer ' + appContext.access_token,
      fhirServer: await this.dataService.getServerUrl(),
      customHeaders: {
        [EndpointService.PAYER_ORG_HEADER]:
          this.endpointService.getPayerOrgIdentifier(),
      },
    };
  }
}
